import { createContext, ReactNode, useContext } from 'react';

/**
 * Which tenant owns the files behind the lesson currently on screen.
 *
 * Every public asset request (`game_images/public/filesV2`, and the passage
 * files behind it) is authorised by an `x-api-tenant` header, and by default
 * that header is derived from the tenant in the URL —
 * `getDestinationTenantFromUrl()`. That is right whenever a teacher is looking
 * at their own lesson, and wrong whenever they are looking at someone else's:
 * the curriculum-fit lessons live in the marketing tenant but are previewed
 * from a page under the *viewer's* tenant, so the backend answers
 * `412 Could not fetch public image` — or `400 Missing tenantId` off a page
 * with no tenant in the path.
 *
 * Context rather than a prop because the request is made four or five
 * components below the one that knows the answer (SlideViewer →
 * SlideElementImage → GameImageRenderer → CroppedGameImageRenderer →
 * useCroppedImage), across two separate subsystems (images and passages), and
 * the failure is silent: nothing throws, the asset simply never appears. A prop
 * would have to be remembered at every new call site; a provider is picked up
 * by all of them, including ones added later.
 *
 * Undefined everywhere else, which keeps the URL-derived default — the designer,
 * live games and a teacher's own library are all unaffected.
 */
const PublicAssetTenantContext = createContext<string | undefined>(undefined);

export const PublicAssetTenantProvider = ({
  tenantId,
  children,
}: {
  tenantId?: string;
  children: ReactNode;
}) => (
  <PublicAssetTenantContext.Provider value={tenantId}>
    {children}
  </PublicAssetTenantContext.Provider>
);

/** The owning tenant for public asset requests, or undefined to use the URL's. */
export const usePublicAssetTenant = (): string | undefined =>
  useContext(PublicAssetTenantContext);
